import { Admin, Resource, CustomRoutes, defaultTheme } from "react-admin";
import { createHashRouter, Route, RouterProvider } from "react-router-dom";
import { JsonImport } from "./jsonImport";
import { ArtistCreate, ArtistEdit, ArtistList, ArtistShow } from "./artists";
import { dataProvider } from "./dataProvider";
import { AlbumCreate, AlbumEdit, AlbumList, AlbumShow } from "./albums";
import { TrackCreate, TrackEdit, TrackList, TrackShow } from "./tracks";
import { authProvider } from "./auth";
import Dashboard from "./dashboard";
import { GenreCreate, GenreEdit, GenreList, GenreShow } from "./genres";
import { AuthCallbackPage, CustomLogin } from "./login";
import { DistributorReportingPage } from "./reporting";
import { initSentry } from "./sentry";
import { CheckForApplicationUpdate, Layout, LayoutProps } from "react-admin";
import indigo from "@mui/material/colors/indigo";
import pink from "@mui/material/colors/pink";
import red from "@mui/material/colors/red";
import { LabelList, LabelEdit, LabelShow, LabelCreate } from "./labels";
import { DDEXMessageCreate, DDEXMessageList, DDEXMessageShow } from "./ddexMessages";
import { DDEXDryRunList, DDEXDryRunShow } from "./ddexDryRuns";
import { UserFeedbackList, UserFeedbackShow } from "./userFeedbacks";
import {
    DDEXGenreMappingCreate,
    DDEXGenreMappingEdit,
    DDEXGenreMappingList,
    DDEXGenreMappingShow,
} from "./ddexGenreMappings";
import { CuratedTracks, FeaturedTracks } from "./featuredTracks";
import { OnboardingSuggestedArtists } from "./onboardingArtists";
import { AppMenu } from "./menu";
import {
    DDEXArtistMappingCreate,
    DDEXArtistMappingEdit,
    DDEXArtistMappingList,
    DDEXArtistMappingShow,
} from "./ddexArtistMappings";
import { ConfigEdit } from "./config";
import { ExportList, ExportShow } from "./exports";
import { DistributorCreate, DistributorEdit, DistributorList, DistributorShow } from "./distributors";
import { captureMessage } from "@sentry/react";
import { PlaylistEdit, PlaylistList, PlaylistShow } from "./playlists";
import { FreeUnlockCreate, FreeUnlockEdit, FreeUnlockList, FreeUnlockShow } from "./freeUnlocks";
import { PromoCodeCreate, PromoCodeEdit, PromoCodeList, PromoCodeShow } from "./promoCodes";
import { BackgroundProcessingList, BackgroundProcessingShow } from "./backgroundProcessings";
import { LLMImport } from "./llmImport";
import { UserList, UserShow } from "./users";
import {
    CMSPermissionCreate,
    CMSPermissionEdit,
    CMSPermissionList,
    CMSPermissionShow,
} from "./cms_permissions";
import { DDEXIssueList, DDEXIssueShow } from "./ddexIssues";
import { FeaturedArtists } from "./featuredArtists";
import { SocialPostCreate, SocialPostEdit, SocialPostList, SocialPostShow } from "./socialPosts";

initSentry();

const VerifyAppUpdateLayout = ({ children, ...props }: LayoutProps) => (
    <Layout {...props} menu={AppMenu}>
        {children}
        <CheckForApplicationUpdate />
    </Layout>
);

let theme = defaultTheme;
if (import.meta.env.MODE !== "production") {
    theme = {
        ...theme,
        palette: {
            primary: indigo,
            secondary: pink,
            error: red,
        },
    };
}

const ExternalResources = [
    <Resource
        name="artists"
        list={ArtistList}
        show={ArtistShow}
        edit={ArtistEdit}
        create={ArtistCreate}
        recordRepresentation="name"
    />,
    <Resource
        name="albums"
        list={AlbumList}
        show={AlbumShow}
        edit={AlbumEdit}
        create={AlbumCreate}
        recordRepresentation="name"
    />,
    <Resource
        name="tracks"
        list={TrackList}
        show={TrackShow}
        edit={TrackEdit}
        create={TrackCreate}
        recordRepresentation="name"
    />,
    <Resource
        name="genres"
        list={GenreList}
        show={GenreShow}
        edit={GenreEdit}
        create={GenreCreate}
        recordRepresentation="name"
    />,
    <Resource
        name="labels"
        list={LabelList}
        show={LabelShow}
        edit={LabelEdit}
        create={LabelCreate}
        recordRepresentation="name"
    />,
];

const AdminResources = [
    <Resource
        name="ddex/messages"
        list={DDEXMessageList}
        show={DDEXMessageShow}
        create={DDEXMessageCreate}
        recordRepresentation="id"
        options={{ label: "DDEX Messages" }}
    />,
    <Resource
        name="ddex/dry_runs"
        list={DDEXDryRunList}
        show={DDEXDryRunShow}
        recordRepresentation="id"
        options={{ label: "DDEX Dry Runs" }}
    />,
    <Resource
        name="ddex/genre_mappings"
        list={DDEXGenreMappingList}
        show={DDEXGenreMappingShow}
        create={DDEXGenreMappingCreate}
        edit={DDEXGenreMappingEdit}
        recordRepresentation="ddex_name"
        options={{ label: "DDEX Genre Mapping" }}
    />,
    <Resource
        name="ddex/artist_mappings"
        list={DDEXArtistMappingList}
        show={DDEXArtistMappingShow}
        create={DDEXArtistMappingCreate}
        edit={DDEXArtistMappingEdit}
        recordRepresentation="name"
        options={{ label: "DDEX Artist Mapping" }}
    />,
    <Resource
        name="ddex/issues"
        list={DDEXIssueList}
        show={DDEXIssueShow}
        recordRepresentation="key"
        options={{ label: "DDEX Issues" }}
    />,
    <Resource
        name="user_feedbacks"
        list={UserFeedbackList}
        show={UserFeedbackShow}
        recordRepresentation="id"
    />,
    <Resource name="exports" list={ExportList} show={ExportShow} recordRepresentation="name" />,
    <Resource
        name="distributors"
        list={DistributorList}
        show={DistributorShow}
        create={DistributorCreate}
        edit={DistributorEdit}
        recordRepresentation="name"
    />,
    <Resource
        name="playlists"
        list={PlaylistList}
        show={PlaylistShow}
        edit={PlaylistEdit}
        recordRepresentation="name"
    />,
    <Resource
        name="free_unlocks"
        list={FreeUnlockList}
        show={FreeUnlockShow}
        create={FreeUnlockCreate}
        edit={FreeUnlockEdit}
        recordRepresentation="id"
    />,
    <Resource
        name="promo_codes"
        list={PromoCodeList}
        show={PromoCodeShow}
        create={PromoCodeCreate}
        edit={PromoCodeEdit}
        recordRepresentation="code"
    />,
    <Resource
        name="background_processings"
        list={BackgroundProcessingList}
        show={BackgroundProcessingShow}
        recordRepresentation="id"
        options={{ label: "Background Processing" }}
    />,
    <Resource name="users" list={UserList} show={UserShow} recordRepresentation="email" />,
    <Resource
        name="cms_permissions"
        list={CMSPermissionList}
        show={CMSPermissionShow}
        create={CMSPermissionCreate}
        edit={CMSPermissionEdit}
        recordRepresentation="email"
        options={{ label: "CMS Permissions" }}
    />,
    <Resource
        name="social_posts"
        list={SocialPostList}
        show={SocialPostShow}
        create={SocialPostCreate}
        edit={SocialPostEdit}
        recordRepresentation="title"
    />,
    <CustomRoutes>
        <Route path="/payments" element={<DistributorReportingPage />} />
        <Route path="/featured_tracks" element={<FeaturedTracks />} />
        <Route path="/curated_tracks" element={<CuratedTracks />} />
        <Route path="/onboarding_artists" element={<OnboardingSuggestedArtists />} />
        <Route path="/featured_artists" element={<FeaturedArtists />} />
        <Route path="/config" element={<ConfigEdit />} />
        <Route path="/json-import" element={<JsonImport />} />
        <Route path="/llm-import" element={<LLMImport />} />
    </CustomRoutes>,
];

const getResources = (permissions: any) => {
    if (!permissions) {
        return [];
    }

    switch (permissions) {
        case "cms_reader":
        case "cms_writer":
        case "superuser":
            return [...ExternalResources, ...AdminResources];
        case "cms_external": {
            return ExternalResources;
        }
    }

    captureMessage(`Unsupported permissions: ${permissions}`);
    return [];
};

export const App = () => {
    const router = createHashRouter([
        {
            path: "/auth-callback",
            element: <AuthCallbackPage />,
        },
        {
            path: "*",
            element: (
                <Admin
                    theme={theme}
                    layout={VerifyAppUpdateLayout}
                    loginPage={CustomLogin}
                    dashboard={Dashboard}
                    dataProvider={dataProvider}
                    authProvider={authProvider}
                    authCallbackPage={false}
                    requireAuth={true}
                    disableTelemetry
                >
                    {getResources}
                </Admin>
            ),
        },
    ]);
    return <RouterProvider router={router} />;
};
